// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Covers the admin core views (audit / channels / usage / users / user
// detail / workspace schedules) and the operator back-office.
import type { LocaleStrings } from './base';

export const adminCoreStrings: LocaleStrings = {
  en: {
    // ── web-ui admin/AuditView.vue ──
    'admin.audit.title':              'Audit log',
    'admin.audit.subtitle':           'Every tool call, message, and credential refresh - hash-chained and tamper-evident (EU AI Act Art. 12).',
    'admin.audit.verify_chain':       'Verify chain',
    'admin.audit.chain_intact':       'Chain intact across {count} events',
    'admin.audit.chain_intact_seq':   ' (seq {first} to {last})',
    'admin.audit.chain_broken':       'Chain broken at sequence {seq}: {reason}',
    'admin.audit.search_placeholder': 'Search target or intent',
    'admin.audit.search_aria':        'Search audit events',
    'admin.audit.facet_kind_aria':    'Filter by kind',
    'admin.audit.facet_outcome_aria': 'Filter by outcome',
    'admin.audit.facet_window_aria':  'Filter by time window',
    'admin.audit.kind_all':           'All kinds',
    'admin.audit.kind_tools':         'Tool calls',
    'admin.audit.kind_messages':      'Messages',
    'admin.audit.kind_credentials':   'Credentials',
    'admin.audit.outcome_all':        'All outcomes',
    'admin.audit.outcome_ok':         'OK',
    'admin.audit.outcome_failed':     'Failed',
    'admin.audit.outcome_denied':     'Denied',
    'admin.audit.window_7d':          '7 days',
    'admin.audit.window_30d':         '30 days',
    'admin.audit.window_90d':         '90 days',
    'admin.audit.window_all':         'All time',
    'admin.audit.empty_title':        'No audit events',
    'admin.audit.empty_body':         'Nothing matches the current filters yet.',
    'admin.audit.col_when':           'When',
    'admin.audit.col_actor':          'Actor',
    'admin.audit.col_kind':           'Kind',
    'admin.audit.col_target':         'Target',
    'admin.audit.col_outcome':        'Outcome',
    'admin.audit.col_latency':        'Latency',
    'admin.audit.actor_system':       'system',
    'admin.audit.detail_seq':         'Sequence',
    'admin.audit.detail_event_id':    'Event ID',
    'admin.audit.detail_agent':       'Agent',
    'admin.audit.detail_on_behalf_of':'On behalf of',
    'admin.audit.detail_conversation':'Conversation',
    'admin.audit.detail_capability':  'Capability',
    'admin.audit.detail_intent':      'Intent',
    'admin.audit.detail_source':      'Source',
    'admin.audit.detail_result_hash': 'Result hash',
    'admin.audit.detail_prev_hash':   'Previous hash',
    'admin.audit.detail_entry_hash':  'Entry hash',
    'admin.audit.args_redacted':      'Arguments (redacted)',
    'admin.audit.pager_range':        '{start}-{end} of {total}',
    'admin.audit.prev_page':          'Previous page',
    'admin.audit.next_page':          'Next page',
    'admin.audit.page_of':            'Page {page} of {total}',
    'admin.audit.prev':               'Prev',
    'admin.audit.next':               'Next',

    // ── web-ui admin/ChannelsView.vue ──
    'admin.channels.title':                'Channels',
    'admin.channels.tab_slack':            'Slack',
    'admin.channels.tab_teams':            'Teams',
    'admin.channels.not_connected_title':  'Slack not connected',
    'admin.channels.not_connected_body':   'Connect Slack to manage the channels HQ can join.',
    'admin.channels.connect_slack':        'Connect Slack',
    'admin.channels.callout_title':        'Add HQ to any channel from Slack',
    'admin.channels.callout_before':       'Type',
    'admin.channels.callout_mid':          'in any channel, or add it from the channel\'s',
    'admin.channels.callout_integrations': 'Integrations',
    'admin.channels.callout_after':        ' settings. Private channels can only be added this way.',
    'admin.channels.add_public_title':     'Add to a public channel',
    'admin.channels.add_public_lede':      'Join HQ to a public channel with one click.',
    'admin.channels.all_public':           'HQ is already in every public channel.',
    'admin.channels.adding':               'Adding…',
    'admin.channels.add_hq':               'Add HQ',
    'admin.channels.present_title':        'Channels HQ is in',
    'admin.channels.present_lede':         'HQ can read and reply in these channels.',
    'admin.channels.no_channels':          'HQ is not in any channels yet.',
    'admin.channels.private':              'Private',
    'admin.channels.added':                'Added HQ to #{name}',
    'admin.channels.add_failed':           'Could not add HQ to #{name}',
    // Teams tab. Unlike Slack, a bot cannot add itself to a team or channel -
    // there's no self-join API - so this tab is instruction-led, not button-led.
    'admin.channels.teams_not_connected_title': 'Teams not connected',
    'admin.channels.teams_not_connected_body':  'Connect Microsoft Teams to see the channels HQ can reach.',
    'admin.channels.connect_teams':        'Connect Teams',
    'admin.channels.teams_callout_title':  'Adding HQ in Teams is done from Teams',
    'admin.channels.teams_callout_body':   'A bot cannot add itself to a team or channel, so there is no button here. Install HQ to a team from the Teams Apps catalog to reach every standard channel. To reach a private or shared channel, open that channel and @mention HQ once - it will appear below.',
    'admin.channels.teams_standard_title': 'Reachable via team install',
    'admin.channels.teams_standard_lede':  'Standard channels HQ reaches automatically wherever it is installed to the team.',
    'admin.channels.teams_added_title':    'Added directly',
    'admin.channels.teams_added_lede':     'Private and shared channels HQ was explicitly added to.',
    'admin.channels.teams_none':           'No channels yet. Install HQ to a team, or @mention it in a private or shared channel.',
    'admin.channels.teams_load_failed':    'Failed to load Teams channels',

    // ── web-ui admin/UsageView.vue ──
    'admin.usage.title':                  'Usage & billing',
    'admin.usage.subtitle':               'Your workspace plan, credit balance, and what your agents are using.',
    'admin.usage.load_error':             'Failed to load usage',
    'admin.usage.toast_payment':          'Payment received - updating your plan.',
    'admin.usage.toast_checkout_canceled':'Checkout canceled.',
    'admin.usage.toast_credits':          'Credits purchased - updating your balance.',
    'admin.usage.toast_topup_canceled':   'Top-up canceled.',
    'admin.usage.checkout_error':         'Could not start checkout',
    'admin.usage.portal_error':           'Could not open the billing portal',
    'admin.usage.topup_error':            'Could not start the top-up',
    'admin.usage.current_plan':           'Current plan: {name}',
    'admin.usage.plan_none_lower':        'no plan',
    'admin.usage.no_plan':                'No plan',
    'admin.usage.plan_word':              'plan',
    'admin.usage.per_month':              '/ ${price} mo',
    'admin.usage.per_month_suffix':       '${price}/mo',
    'admin.usage.per_month_label':        'per month',
    'admin.usage.upgrade_to':             'Upgrade to {name}',
    'admin.usage.manage_billing':         'Manage billing',
    'admin.usage.credits':                'Credits',
    'admin.usage.credits_available':      'available',
    'admin.usage.out_of_credits':         'Out of credits - new work is paused.',
    'admin.usage.included_per_period':    '{count} included per period',
    'admin.usage.used_all_time':          '{count} used all-time',
    'admin.usage.apps':                   'Apps',
    'admin.usage.unit_apps':              'of {max}',
    'admin.usage.note_apps':              'Persistent app surfaces on the {plan} plan.',
    'admin.usage.schedules':              'Schedules',
    'admin.usage.unit_schedules':         'of {max}',
    'admin.usage.note_schedules':         'Recurring tasks on the {plan} plan.',
    'admin.usage.inboxes':                'Email inboxes',
    'admin.usage.unit_inboxes':           'of {max}',
    'admin.usage.note_inboxes':           'Email endpoints on the {plan} plan.',
    'admin.usage.workspaces':             'Workspaces',
    'admin.usage.unit_workspaces':        'of {max}',
    'admin.usage.note_workspaces':        'Workspaces under this account.',
    'admin.usage.upgrade_eyebrow':        'Upgrade',
    'admin.usage.do_more':                'Do more with {name}',
    'admin.usage.free_plan_pitch':        'Step up to {name} for more credits, apps, schedules, and email inboxes.',
    'admin.usage.feat_credits':           'Credits',
    'admin.usage.buy_credits':            'Buy credits',
    'admin.usage.buy_credits_sub':        'One-time packs that top up your balance. They never expire.',
    'admin.usage.pack_suffix':            'credits - ${price}',
    'admin.usage.model_mix':              'Model mix',
    'admin.usage.last_days':              'last {count} days',
    'admin.usage.col_model':              'Model',
    'admin.usage.col_provider':           'Provider',
    'admin.usage.col_turns':              'Turns',
    'admin.usage.col_input_tok':          'Input tokens',
    'admin.usage.col_output_tok':         'Output tokens',
    'admin.usage.col_cost':               'Cost',
    'admin.usage.top_tools':              'Top tools',
    'admin.usage.no_tools_title':         'No tool usage yet',
    'admin.usage.no_tools_body':          'Tool calls your agents make will show up here.',
    'admin.usage.col_tool':               'Tool',
    'admin.usage.col_integration':        'Integration',
    'admin.usage.col_calls':              'Calls',
    'admin.usage.col_errors':             'Errors',
    'admin.usage.activity':               'Activity',
    'admin.usage.col_activity':           'Activity',
    'admin.usage.col_conversations':      'Conversations',
    'admin.usage.col_tokens':             'Tokens',
    'admin.usage.col_total_tokens':       'Total tokens',
    'admin.usage.col_cache':              'Cache tokens',
    'admin.usage.top_consumers':          'Top consumers',
    'admin.usage.col_name':               'Name',
    'admin.usage.col_kind':               'Kind',
    'admin.usage.kind_schedule':          'Schedule',
    'admin.usage.kind_conversation':      'Conversation',

    // ── web-ui admin/UsersView.vue ──
    'admin.users.title':                  'Users',
    'admin.users.subtitle':               'Everyone mapped to this workspace via Slack, Teams, or invite.',
    'admin.users.tab_active':             'Active',
    'admin.users.tab_removed':            'Removed',
    'admin.users.tab_all':                'All',
    'admin.users.filter_status_aria':     'Filter by status',
    'admin.users.invite_people':          'Invite people',
    'admin.users.search_placeholder':     'Search by name, handle, or email',
    'admin.users.search_aria':            'Search users',
    'admin.users.count_one':              '{count} user',
    'admin.users.count_other':            '{count} users',
    'admin.users.col_name':               'Name',
    'admin.users.col_connected_as':       'Connected as',
    'admin.users.col_email':              'Email',
    'admin.users.col_last_active':        'Last active',
    'admin.users.col_status':             'Status',
    'admin.users.open_user':              'Open {name}',
    'admin.users.badge_removed':          'Removed',
    'admin.users.badge_bot':              'Bot',
    'admin.users.badge_admin':            'Admin',
    'admin.users.badge_member':           'Member',
    'admin.users.badge_invited':          'Invited',
    'admin.users.badge_guest':            'Guest',
    'admin.users.tip_invited':            'Invited by email, not yet signed in.',
    'admin.users.tip_guest':              'Slack restricted guest (single- or multi-channel).',
    'admin.users.empty_no_matches_title': 'No matches',
    'admin.users.empty_no_matches_body':  'No users match your search. Try a different term.',
    'admin.users.empty_removed_title':    'No removed users',
    'admin.users.empty_removed_body':     'Nobody has been removed from this workspace.',
    'admin.users.empty_title':            'No users yet',
    'admin.users.empty_body':             'Users appear here once they sign in or are invited.',

    // ── web-ui admin/UserDetailView.vue ──
    'admin.userDetail.title_fallback':       'User',
    'admin.userDetail.load_error':           'Failed to load user',
    'admin.userDetail.sending':              'Sending…',
    'admin.userDetail.resend_invite':        'Resend invite',
    'admin.userDetail.revoke':               'Revoke',
    'admin.userDetail.erasing':              'Erasing…',
    'admin.userDetail.erase_user':           'Erase user',
    'admin.userDetail.badge_deleted':        'Removed',
    'admin.userDetail.badge_bot':            'Bot',
    'admin.userDetail.badge_admin':          'Admin',
    'admin.userDetail.badge_member':         'Member',
    'admin.userDetail.badge_invited':        'Invited',
    'admin.userDetail.badge_guest':          'Guest',
    'admin.userDetail.identity':             'Identity',
    'admin.userDetail.connected_as':         'Connected as',
    'admin.userDetail.email':                'Email',
    'admin.userDetail.user_title':           'Title',
    'admin.userDetail.origin':               'Origin',
    'admin.userDetail.locale':               'Locale',
    'admin.userDetail.timezone':             'Timezone',
    'admin.userDetail.last_active':          'Last active',
    'admin.userDetail.access':               'Access',
    'admin.userDetail.signin':               'Sign-in',
    'admin.userDetail.signin_hint':          'Whether this person can sign in to HQ. Default follows the workspace policy.',
    'admin.userDetail.signin_access_aria':   'Sign-in access',
    'admin.userDetail.can_signin':           'Can sign in',
    'admin.userDetail.blocked':              'Blocked',
    'admin.userDetail.access_default':       'Default',
    'admin.userDetail.access_allow':         'Allow',
    'admin.userDetail.access_deny':          'Deny',
    'admin.userDetail.admin_role':           'Admin role',
    'admin.userDetail.admin_role_hint':      'Grant or revoke workspace admin. Default follows the Slack admin flag.',
    'admin.userDetail.admin_role_aria':      'Admin role',
    'admin.userDetail.admin_default':        'Default',
    'admin.userDetail.admin_grant':          'Grant',
    'admin.userDetail.admin_revoke':         'Revoke',
    'admin.userDetail.tenant_admin':         'Workspace admin',
    'admin.userDetail.member':               'Member',
    'admin.userDetail.admin_tip_self':       'You cannot revoke your own admin role.',
    'admin.userDetail.admin_tip_granted':    'Admin granted regardless of the source workspace.',
    'admin.userDetail.admin_tip_revoked':    'Admin revoked regardless of the source workspace.',
    'admin.userDetail.admin_tip_default':    'Follows the source workspace admin flag.',
    'admin.userDetail.why_blocked_admin':    'blocked by an admin',
    'admin.userDetail.why_allowed_admin':    'allowed by an admin',
    'admin.userDetail.why_guest_blocked':    'guests are blocked by workspace policy',
    'admin.userDetail.why_approval_pending': 'workspace requires approval',
    'admin.userDetail.why_member':           'workspace member',
    'admin.userDetail.signin_set':           'Set {name} sign-in to {access}',
    'admin.userDetail.signin_update_error':  'Could not update sign-in access',
    'admin.userDetail.admin_set':            'Set {name} admin role to {access}',
    'admin.userDetail.admin_update_error':   'Could not update admin role',
    'admin.userDetail.invite_resent':        'Invite resent to {email}',
    'admin.userDetail.invite_refreshed':     'Invite link refreshed for {email}',
    'admin.userDetail.resend_error':         'Could not resend the invite',
    'admin.userDetail.revoke_title':         'Revoke invite?',
    'admin.userDetail.revoke_body':          'This cancels the pending invite for {email}. They will no longer be able to use the link.',
    'admin.userDetail.revoke_confirm':       'Revoke invite',
    'admin.userDetail.invite_revoked':       'Invite revoked for {email}',
    'admin.userDetail.revoke_error':         'Could not revoke the invite',
    'admin.userDetail.erase_self_warn':      'Use your own account settings to erase your data.',
    'admin.userDetail.erase_title':          'Erase user?',
    'admin.userDetail.erase_body':           'This permanently erases {name} and redacts their messages, memory, and credentials. This cannot be undone.',
    'admin.userDetail.erase_confirm':        'Erase user',
    'admin.userDetail.erase_done':           'Erased {name}: {msgs} messages, {facts} facts, {rows} corpus rows, {creds} credentials.',
    'admin.userDetail.erase_error':          'Could not erase the user',

    // ── web-ui admin/WorkspaceSchedulesView.vue ──
    'admin.workspaceSchedules.title':           'Workspace schedules',
    'admin.workspaceSchedules.new_schedule':    'New schedule',
    'admin.workspaceSchedules.all_users':       'All users',
    'admin.workspaceSchedules.unknown_owner':   'Unknown owner',
    'admin.workspaceSchedules.this_schedule':   'this schedule',
    'admin.workspaceSchedules.its_owner':       'its owner',
    'admin.workspaceSchedules.filter_aria':     'Filter schedules',
    'admin.workspaceSchedules.tab_active':      'Active',
    'admin.workspaceSchedules.tab_all':         'All',
    'admin.workspaceSchedules.user':            'User',
    'admin.workspaceSchedules.filter_user_aria':'Filter by user',
    'admin.workspaceSchedules.cancel_title':    'Cancel schedule?',
    'admin.workspaceSchedules.cancel_body':     'Stop "{name}", owned by {owner}? It will no longer run.',
    'admin.workspaceSchedules.cancel_confirm':  'Cancel schedule',
    'admin.workspaceSchedules.cancel_keep':     'Keep it',
    'admin.workspaceSchedules.erase_title':     'Erase schedule?',
    'admin.workspaceSchedules.erase_body':      'This permanently deletes "{name}" and its run history. This cannot be undone.',
    'admin.workspaceSchedules.erase_confirm':   'Erase schedule',
    'admin.workspaceSchedules.erase_keep':      'Keep it',
    'admin.workspaceSchedules.erase_error':     'Could not erase the schedule',
    'admin.workspaceSchedules.empty_user_title':'No schedules for this user',
    'admin.workspaceSchedules.empty_title':     'No schedules yet',
    'admin.workspaceSchedules.empty_body':      'Scheduled tasks created in this workspace will show up here.',

    // ── web-ui operator/OperatorView.vue ──
    'operator.title':                     'Operator',
    'operator.subtitle':                  'Platform back-office: signups, invites, and workspaces.',
    'operator.section_aria':              'Operator section',
    'operator.help_docs':                 'Help docs',
    'operator.tab_signups':               'Signups',
    'operator.tab_invites':               'Invites',
    'operator.tab_tenants':               'Workspaces',
    'operator.copied':                    'Copied to clipboard',
    'operator.copy_failed':               'Could not copy to clipboard',
    'operator.copy':                      'Copy',
    'operator.dismiss':                   'Dismiss',
    'operator.stat_new_signups':          'New signups',
    'operator.stat_open_invites':         'Open invites',
    'operator.stat_tenants':              'Workspaces',
    'operator.stat_users':                'Users',
    // Signups
    'operator.req_new':                   'New',
    'operator.req_invited':               'Invited',
    'operator.req_rejected':              'Rejected',
    'operator.req_spam':                  'Spam',
    'operator.req_all':                   'All',
    'operator.filter_signups_aria':       'Filter signups',
    'operator.search_signups_placeholder':'Search name, email, or company',
    'operator.search_signups_aria':       'Search signups',
    'operator.col_who':                   'Who',
    'operator.col_company':               'Company',
    'operator.col_channel':               'Channel',
    'operator.col_when':                  'When',
    'operator.col_status':                'Status',
    'operator.invite':                    'Invite',
    'operator.reject':                    'Reject',
    'operator.mark_spam':                 'Mark as spam',
    'operator.no_signups':                'No signups',
    'operator.no_signups_new':            'No new signup requests right now.',
    'operator.no_signups_filtered':       'No signups match this filter.',
    'operator.invite_create_error':       'Could not create the invite',
    'operator.invite_emailed_to':         'Invite emailed to {email}',
    'operator.invite_created':            'Invite created',
    'operator.signup_link_for':           'Signup link for {email}',
    'operator.marked':                    'Marked as {status}',
    'operator.update_error':              'Could not update the request',
    // Invites
    'operator.inv_active':                'Active',
    'operator.inv_revoked':               'Revoked',
    'operator.inv_all':                   'All',
    'operator.filter_invites_aria':       'Filter invites',
    'operator.new_invite':                'New invite',
    'operator.field_note':                'Note',
    'operator.field_note_hint':           'For your own reference - who or what this invite is for.',
    'operator.field_max_uses':            'Max uses',
    'operator.field_expires':             'Expires in days',
    'operator.field_expires_hint':        '0 means it never expires.',
    'operator.field_email_to':            'Email to',
    'operator.field_email_to_hint':       'Optional - email the invite link directly.',
    'operator.create_invite':             'Create invite',
    'operator.invite_emailed':            'Invite emailed',
    'operator.signup_link':               'Signup link',
    'operator.col_code':                  'Code',
    'operator.col_note':                  'Note',
    'operator.col_uses':                  'Uses',
    'operator.col_expires':               'Expires',
    'operator.col_state':                 'State',
    'operator.col_created':               'Created',
    'operator.copy_code':                 'Copy code',
    'operator.joined':                    '{count} joined',
    'operator.never':                     'Never',
    'operator.state_revoked':             'Revoked',
    'operator.state_expired':             'Expired',
    'operator.state_used_up':             'Used up',
    'operator.state_active':              'Active',
    'operator.revoke':                    'Revoke',
    'operator.revoke_title':              'Revoke invite?',
    'operator.revoke_body':               'Revoke invite code {code}? It can no longer be redeemed.',
    'operator.revoke_confirm':            'Revoke',
    'operator.invite_revoked':            'Invite revoked',
    'operator.revoke_error':              'Could not revoke the invite',
    'operator.no_invites':                'No invites',
    'operator.no_invites_body':           'Mint an invite code to let someone create a workspace.',
    // Tenants
    'operator.search_tenants_placeholder':'Search workspaces',
    'operator.search_tenants_aria':       'Search workspaces',
    'operator.new_workspace':             'New workspace',
    'operator.field_workspace_name':      'Workspace name',
    'operator.field_owner_email':         'Owner email',
    'operator.field_owner_email_hint':    'The first admin of the new workspace.',
    'operator.field_plan_slug':           'Plan',
    'operator.field_plan_slug_hint':      'Optional - defaults to the free plan.',
    'operator.field_website':             'Website',
    'operator.field_website_hint':        'The company domain, used for branding.',
    'operator.field_workspace_url':       'Workspace URL',
    'operator.field_workspace_url_hint':  'Optional - the slug in the workspace URL.',
    'operator.field_access':              'Access',
    'operator.field_join_as_admin':       'Join as admin',
    'operator.field_integrations':        'Integrations',
    'operator.field_enable_tic':          'Enable TIC company intelligence (free)',
    'operator.field_tic_hint':            'Seals the platform TIC key into the new workspace and turns the integration on.',
    'operator.tic_enable_failed':         'Workspace created, but the TIC integration could not be enabled (check HQ_TIC_FREEBIE_API_KEY).',
    'operator.create_workspace':          'Create workspace',
    'operator.err_name_required':         'A workspace name is required.',
    'operator.err_owner_required':        'A valid owner email is required.',
    'operator.err_website_required':      'A valid website is required.',
    'operator.workspace_create_error':    'Could not create the workspace',
    'operator.workspace_created_emailed': 'Workspace {slug} created - owner emailed.',
    'operator.workspace_created':         'Workspace {slug} created',
    'operator.open_as_admin':             'Open {slug} as admin',
    'operator.owner_signin_link':         'Owner sign-in link for {slug}',
    'operator.col_workspace':             'Workspace',
    'operator.col_plan':                  'Plan',
    'operator.col_source':                'Source',
    'operator.col_users':                 'Users',
    'operator.tag_platform':              'Platform',
    'operator.tag_demo':                  'Demo',
    'operator.tag_onboarding':            'Onboarding',
    'operator.add_user':                  'Add user',
    'operator.field_email':               'Email',
    'operator.field_display_name':        'Display name',
    'operator.field_role':                'Role',
    'operator.field_workspace_admin':     'Workspace admin',
    'operator.send_invite':               'Send invite',
    'operator.cancel':                    'Cancel',
    'operator.err_email_required':        'A valid email is required.',
    'operator.add_user_error':            'Could not add the user',
    'operator.user_invited':              '{email} invited to {slug}',
    'operator.user_added':                '{email} added to {slug}',
    'operator.user_signin_link':          'Sign-in link for {email}',
    'operator.no_tenants':                'No workspaces',
    'operator.no_tenants_body':           'Create a workspace to onboard a company.',
  },
  sv: {
    // ── web-ui admin/AuditView.vue ──
    'admin.audit.title':              'Granskningslogg',
    'admin.audit.subtitle':           'Varje verktygsanrop, meddelande och uppdaterad behörighet - hashkedjad och manipuleringssäker (EU:s AI-förordning art. 12).',
    'admin.audit.verify_chain':       'Verifiera kedja',
    'admin.audit.chain_intact':       'Kedjan är intakt över {count} händelser',
    'admin.audit.chain_intact_seq':   ' (sekvens {first} till {last})',
    'admin.audit.chain_broken':       'Kedjan bruten vid sekvens {seq}: {reason}',
    'admin.audit.search_placeholder': 'Sök mål eller avsikt',
    'admin.audit.search_aria':        'Sök granskningshändelser',
    'admin.audit.facet_kind_aria':    'Filtrera på typ',
    'admin.audit.facet_outcome_aria': 'Filtrera på utfall',
    'admin.audit.facet_window_aria':  'Filtrera på tidsfönster',
    'admin.audit.kind_all':           'Alla typer',
    'admin.audit.kind_tools':         'Verktygsanrop',
    'admin.audit.kind_messages':      'Meddelanden',
    'admin.audit.kind_credentials':   'Behörigheter',
    'admin.audit.outcome_all':        'Alla utfall',
    'admin.audit.outcome_ok':         'OK',
    'admin.audit.outcome_failed':     'Misslyckades',
    'admin.audit.outcome_denied':     'Nekad',
    'admin.audit.window_7d':          '7 dagar',
    'admin.audit.window_30d':         '30 dagar',
    'admin.audit.window_90d':         '90 dagar',
    'admin.audit.window_all':         'All tid',
    'admin.audit.empty_title':        'Inga granskningshändelser',
    'admin.audit.empty_body':         'Inget matchar de nuvarande filtren än.',
    'admin.audit.col_when':           'När',
    'admin.audit.col_actor':          'Aktör',
    'admin.audit.col_kind':           'Typ',
    'admin.audit.col_target':         'Mål',
    'admin.audit.col_outcome':        'Utfall',
    'admin.audit.col_latency':        'Svarstid',
    'admin.audit.actor_system':       'system',
    'admin.audit.detail_seq':         'Sekvens',
    'admin.audit.detail_event_id':    'Händelse-ID',
    'admin.audit.detail_agent':       'Agent',
    'admin.audit.detail_on_behalf_of':'På uppdrag av',
    'admin.audit.detail_conversation':'Konversation',
    'admin.audit.detail_capability':  'Behörighet',
    'admin.audit.detail_intent':      'Avsikt',
    'admin.audit.detail_source':      'Källa',
    'admin.audit.detail_result_hash': 'Resultathash',
    'admin.audit.detail_prev_hash':   'Föregående hash',
    'admin.audit.detail_entry_hash':  'Posthash',
    'admin.audit.args_redacted':      'Argument (maskerade)',
    'admin.audit.pager_range':        '{start}-{end} av {total}',
    'admin.audit.prev_page':          'Föregående sida',
    'admin.audit.next_page':          'Nästa sida',
    'admin.audit.page_of':            'Sida {page} av {total}',
    'admin.audit.prev':               'Föreg.',
    'admin.audit.next':               'Nästa',

    // ── web-ui admin/ChannelsView.vue ──
    'admin.channels.title':                'Kanaler',
    'admin.channels.tab_slack':            'Slack',
    'admin.channels.tab_teams':            'Teams',
    'admin.channels.not_connected_title':  'Slack inte ansluten',
    'admin.channels.not_connected_body':   'Anslut Slack för att hantera kanalerna HQ kan gå med i.',
    'admin.channels.connect_slack':        'Anslut Slack',
    'admin.channels.callout_title':        'Lägg till HQ i valfri kanal från Slack',
    'admin.channels.callout_before':       'Skriv',
    'admin.channels.callout_mid':          'i valfri kanal, eller lägg till det från kanalens',
    'admin.channels.callout_integrations': 'Integrationer',
    'admin.channels.callout_after':        '-inställningar. Privata kanaler kan bara läggas till på det här sättet.',
    'admin.channels.add_public_title':     'Lägg till i en publik kanal',
    'admin.channels.add_public_lede':      'Lägg HQ i en publik kanal med ett klick.',
    'admin.channels.all_public':           'HQ finns redan i alla publika kanaler.',
    'admin.channels.adding':               'Lägger till…',
    'admin.channels.add_hq':               'Lägg till HQ',
    'admin.channels.present_title':        'Kanaler HQ är med i',
    'admin.channels.present_lede':         'HQ kan läsa och svara i de här kanalerna.',
    'admin.channels.no_channels':          'HQ finns inte i några kanaler än.',
    'admin.channels.private':              'Privat',
    'admin.channels.added':                'Lade till HQ i #{name}',
    'admin.channels.add_failed':           'Det gick inte att lägga till HQ i #{name}',
    // Teams-flik. Till skillnad från Slack kan en bot inte lägga till sig själv i
    // ett team eller en kanal - det finns inget självanslut-API - så fliken är
    // instruktionsdriven, inte knappdriven.
    'admin.channels.teams_not_connected_title': 'Teams inte anslutet',
    'admin.channels.teams_not_connected_body':  'Anslut Microsoft Teams för att se kanalerna HQ kan nå.',
    'admin.channels.connect_teams':        'Anslut Teams',
    'admin.channels.teams_callout_title':  'Att lägga till HQ i Teams görs från Teams',
    'admin.channels.teams_callout_body':   'En bot kan inte lägga till sig själv i ett team eller en kanal, så det finns ingen knapp här. Installera HQ i ett team från Teams appkatalog för att nå alla standardkanaler. För att nå en privat eller delad kanal, öppna den kanalen och @nämn HQ en gång - den dyker upp nedan.',
    'admin.channels.teams_standard_title': 'Nås via teaminstallation',
    'admin.channels.teams_standard_lede':  'Standardkanaler HQ når automatiskt där det är installerat i teamet.',
    'admin.channels.teams_added_title':    'Tillagda direkt',
    'admin.channels.teams_added_lede':     'Privata och delade kanaler HQ uttryckligen lagts till i.',
    'admin.channels.teams_none':           'Inga kanaler än. Installera HQ i ett team, eller @nämn det i en privat eller delad kanal.',
    'admin.channels.teams_load_failed':    'Det gick inte att läsa in Teams-kanaler.',

    // ── web-ui admin/UsageView.vue ──
    'admin.usage.title':                  'Användning och fakturering',
    'admin.usage.subtitle':               'Din arbetsytas plan, kreditsaldo och vad dina agenter använder.',
    'admin.usage.load_error':             'Det gick inte att läsa in användning',
    'admin.usage.toast_payment':          'Betalning mottagen - uppdaterar din plan.',
    'admin.usage.toast_checkout_canceled':'Kassan avbröts.',
    'admin.usage.toast_credits':          'Krediter köpta - uppdaterar ditt saldo.',
    'admin.usage.toast_topup_canceled':   'Påfyllningen avbröts.',
    'admin.usage.checkout_error':         'Det gick inte att starta kassan',
    'admin.usage.portal_error':           'Det gick inte att öppna faktureringsportalen',
    'admin.usage.topup_error':            'Det gick inte att starta påfyllningen',
    'admin.usage.current_plan':           'Aktuell plan: {name}',
    'admin.usage.plan_none_lower':        'ingen plan',
    'admin.usage.no_plan':                'Ingen plan',
    'admin.usage.plan_word':              'planen',
    'admin.usage.per_month':              '/ ${price} mån',
    'admin.usage.per_month_suffix':       '${price}/mån',
    'admin.usage.per_month_label':        'per månad',
    'admin.usage.upgrade_to':             'Uppgradera till {name}',
    'admin.usage.manage_billing':         'Hantera fakturering',
    'admin.usage.credits':                'Krediter',
    'admin.usage.credits_available':      'tillgängliga',
    'admin.usage.out_of_credits':         'Slut på krediter - nytt arbete är pausat.',
    'admin.usage.included_per_period':    '{count} ingår per period',
    'admin.usage.used_all_time':          '{count} använda totalt',
    'admin.usage.apps':                   'Appar',
    'admin.usage.unit_apps':              'av {max}',
    'admin.usage.note_apps':              'Beständiga appytor på {plan}.',
    'admin.usage.schedules':              'Scheman',
    'admin.usage.unit_schedules':         'av {max}',
    'admin.usage.note_schedules':         'Återkommande uppgifter på {plan}.',
    'admin.usage.inboxes':                'E-postinkorgar',
    'admin.usage.unit_inboxes':           'av {max}',
    'admin.usage.note_inboxes':           'E-postendpunkter på {plan}.',
    'admin.usage.workspaces':             'Arbetsytor',
    'admin.usage.unit_workspaces':        'av {max}',
    'admin.usage.note_workspaces':        'Arbetsytor under det här kontot.',
    'admin.usage.upgrade_eyebrow':        'Uppgradera',
    'admin.usage.do_more':                'Gör mer med {name}',
    'admin.usage.free_plan_pitch':        'Gå upp till {name} för fler krediter, appar, scheman och e-postinkorgar.',
    'admin.usage.feat_credits':           'Krediter',
    'admin.usage.buy_credits':            'Köp krediter',
    'admin.usage.buy_credits_sub':        'Engångspaket som fyller på ditt saldo. De går aldrig ut.',
    'admin.usage.pack_suffix':            'krediter - ${price}',
    'admin.usage.model_mix':              'Modellmix',
    'admin.usage.last_days':              'senaste {count} dagarna',
    'admin.usage.col_model':              'Modell',
    'admin.usage.col_provider':           'Leverantör',
    'admin.usage.col_turns':              'Turer',
    'admin.usage.col_input_tok':          'Indatatokens',
    'admin.usage.col_output_tok':         'Utdatatokens',
    'admin.usage.col_cost':               'Kostnad',
    'admin.usage.top_tools':              'Mest använda verktyg',
    'admin.usage.no_tools_title':         'Ingen verktygsanvändning än',
    'admin.usage.no_tools_body':          'Verktygsanrop dina agenter gör visas här.',
    'admin.usage.col_tool':               'Verktyg',
    'admin.usage.col_integration':        'Integration',
    'admin.usage.col_calls':              'Anrop',
    'admin.usage.col_errors':             'Fel',
    'admin.usage.activity':               'Aktivitet',
    'admin.usage.col_activity':           'Aktivitet',
    'admin.usage.col_conversations':      'Konversationer',
    'admin.usage.col_tokens':             'Tokens',
    'admin.usage.col_total_tokens':       'Totala tokens',
    'admin.usage.col_cache':              'Cachetokens',
    'admin.usage.top_consumers':          'Största förbrukare',
    'admin.usage.col_name':               'Namn',
    'admin.usage.col_kind':               'Typ',
    'admin.usage.kind_schedule':          'Schema',
    'admin.usage.kind_conversation':      'Konversation',

    // ── web-ui admin/UsersView.vue ──
    'admin.users.title':                  'Användare',
    'admin.users.subtitle':               'Alla som är kopplade till den här arbetsytan via Slack, Teams eller inbjudan.',
    'admin.users.tab_active':             'Aktiva',
    'admin.users.tab_removed':            'Borttagna',
    'admin.users.tab_all':                'Alla',
    'admin.users.filter_status_aria':     'Filtrera på status',
    'admin.users.invite_people':          'Bjud in personer',
    'admin.users.search_placeholder':     'Sök på namn, handle eller e-post',
    'admin.users.search_aria':            'Sök användare',
    'admin.users.count_one':              '{count} användare',
    'admin.users.count_other':            '{count} användare',
    'admin.users.col_name':               'Namn',
    'admin.users.col_connected_as':       'Ansluten som',
    'admin.users.col_email':              'E-post',
    'admin.users.col_last_active':        'Senast aktiv',
    'admin.users.col_status':             'Status',
    'admin.users.open_user':              'Öppna {name}',
    'admin.users.badge_removed':          'Borttagen',
    'admin.users.badge_bot':              'Bot',
    'admin.users.badge_admin':            'Administratör',
    'admin.users.badge_member':           'Medlem',
    'admin.users.badge_invited':          'Inbjuden',
    'admin.users.badge_guest':            'Gäst',
    'admin.users.tip_invited':            'Inbjuden via e-post, har inte loggat in än.',
    'admin.users.tip_guest':              'Begränsad Slack-gäst (en eller flera kanaler).',
    'admin.users.empty_no_matches_title': 'Inga träffar',
    'admin.users.empty_no_matches_body':  'Inga användare matchar din sökning. Prova en annan term.',
    'admin.users.empty_removed_title':    'Inga borttagna användare',
    'admin.users.empty_removed_body':     'Ingen har tagits bort från den här arbetsytan.',
    'admin.users.empty_title':            'Inga användare än',
    'admin.users.empty_body':             'Användare visas här när de loggar in eller bjuds in.',

    // ── web-ui admin/UserDetailView.vue ──
    'admin.userDetail.title_fallback':       'Användare',
    'admin.userDetail.load_error':           'Det gick inte att läsa in användaren',
    'admin.userDetail.sending':              'Skickar…',
    'admin.userDetail.resend_invite':        'Skicka inbjudan igen',
    'admin.userDetail.revoke':               'Återkalla',
    'admin.userDetail.erasing':              'Raderar…',
    'admin.userDetail.erase_user':           'Radera användare',
    'admin.userDetail.badge_deleted':        'Borttagen',
    'admin.userDetail.badge_bot':            'Bot',
    'admin.userDetail.badge_admin':          'Administratör',
    'admin.userDetail.badge_member':         'Medlem',
    'admin.userDetail.badge_invited':        'Inbjuden',
    'admin.userDetail.badge_guest':          'Gäst',
    'admin.userDetail.identity':             'Identitet',
    'admin.userDetail.connected_as':         'Ansluten som',
    'admin.userDetail.email':                'E-post',
    'admin.userDetail.user_title':           'Titel',
    'admin.userDetail.origin':               'Ursprung',
    'admin.userDetail.locale':               'Språk',
    'admin.userDetail.timezone':             'Tidszon',
    'admin.userDetail.last_active':          'Senast aktiv',
    'admin.userDetail.access':               'Åtkomst',
    'admin.userDetail.signin':               'Inloggning',
    'admin.userDetail.signin_hint':          'Om den här personen kan logga in på HQ. Standard följer arbetsytans policy.',
    'admin.userDetail.signin_access_aria':   'Inloggningsåtkomst',
    'admin.userDetail.can_signin':           'Kan logga in',
    'admin.userDetail.blocked':              'Blockerad',
    'admin.userDetail.access_default':       'Standard',
    'admin.userDetail.access_allow':         'Tillåt',
    'admin.userDetail.access_deny':          'Neka',
    'admin.userDetail.admin_role':           'Administratörsroll',
    'admin.userDetail.admin_role_hint':      'Tilldela eller återkalla arbetsyteadministratör. Standard följer Slack-administratörsflaggan.',
    'admin.userDetail.admin_role_aria':      'Administratörsroll',
    'admin.userDetail.admin_default':        'Standard',
    'admin.userDetail.admin_grant':          'Tilldela',
    'admin.userDetail.admin_revoke':         'Återkalla',
    'admin.userDetail.tenant_admin':         'Arbetsyteadministratör',
    'admin.userDetail.member':               'Medlem',
    'admin.userDetail.admin_tip_self':       'Du kan inte återkalla din egen administratörsroll.',
    'admin.userDetail.admin_tip_granted':    'Administratör tilldelad oavsett källarbetsyta.',
    'admin.userDetail.admin_tip_revoked':    'Administratör återkallad oavsett källarbetsyta.',
    'admin.userDetail.admin_tip_default':    'Följer källarbetsytans administratörsflagga.',
    'admin.userDetail.why_blocked_admin':    'blockerad av en administratör',
    'admin.userDetail.why_allowed_admin':    'tillåten av en administratör',
    'admin.userDetail.why_guest_blocked':    'gäster blockeras av arbetsytans policy',
    'admin.userDetail.why_approval_pending': 'arbetsytan kräver godkännande',
    'admin.userDetail.why_member':           'arbetsytemedlem',
    'admin.userDetail.signin_set':           'Satte {name} inloggning till {access}',
    'admin.userDetail.signin_update_error':  'Det gick inte att uppdatera inloggningsåtkomsten',
    'admin.userDetail.admin_set':            'Satte {name} administratörsroll till {access}',
    'admin.userDetail.admin_update_error':   'Det gick inte att uppdatera administratörsrollen',
    'admin.userDetail.invite_resent':        'Inbjudan skickades igen till {email}',
    'admin.userDetail.invite_refreshed':     'Inbjudningslänk uppdaterad för {email}',
    'admin.userDetail.resend_error':         'Det gick inte att skicka inbjudan igen',
    'admin.userDetail.revoke_title':         'Återkalla inbjudan?',
    'admin.userDetail.revoke_body':          'Detta avbryter den väntande inbjudan för {email}. De kommer inte längre att kunna använda länken.',
    'admin.userDetail.revoke_confirm':       'Återkalla inbjudan',
    'admin.userDetail.invite_revoked':       'Inbjudan återkallad för {email}',
    'admin.userDetail.revoke_error':         'Det gick inte att återkalla inbjudan',
    'admin.userDetail.erase_self_warn':      'Använd dina egna kontoinställningar för att radera dina data.',
    'admin.userDetail.erase_title':          'Radera användare?',
    'admin.userDetail.erase_body':           'Detta raderar permanent {name} och maskerar deras meddelanden, minne och behörigheter. Detta kan inte ångras.',
    'admin.userDetail.erase_confirm':        'Radera användare',
    'admin.userDetail.erase_done':           'Raderade {name}: {msgs} meddelanden, {facts} fakta, {rows} korpusrader, {creds} behörigheter.',
    'admin.userDetail.erase_error':          'Det gick inte att radera användaren',

    // ── web-ui admin/WorkspaceSchedulesView.vue ──
    'admin.workspaceSchedules.title':           'Arbetsytans scheman',
    'admin.workspaceSchedules.new_schedule':    'Nytt schema',
    'admin.workspaceSchedules.all_users':       'Alla användare',
    'admin.workspaceSchedules.unknown_owner':   'Okänd ägare',
    'admin.workspaceSchedules.this_schedule':   'det här schemat',
    'admin.workspaceSchedules.its_owner':       'dess ägare',
    'admin.workspaceSchedules.filter_aria':     'Filtrera scheman',
    'admin.workspaceSchedules.tab_active':      'Aktiva',
    'admin.workspaceSchedules.tab_all':         'Alla',
    'admin.workspaceSchedules.user':            'Användare',
    'admin.workspaceSchedules.filter_user_aria':'Filtrera på användare',
    'admin.workspaceSchedules.cancel_title':    'Avbryt schema?',
    'admin.workspaceSchedules.cancel_body':     'Stoppa "{name}", som ägs av {owner}? Det kommer inte längre att köras.',
    'admin.workspaceSchedules.cancel_confirm':  'Avbryt schema',
    'admin.workspaceSchedules.cancel_keep':     'Behåll det',
    'admin.workspaceSchedules.erase_title':     'Radera schema?',
    'admin.workspaceSchedules.erase_body':      'Detta tar permanent bort "{name}" och dess körhistorik. Detta kan inte ångras.',
    'admin.workspaceSchedules.erase_confirm':   'Radera schema',
    'admin.workspaceSchedules.erase_keep':      'Behåll det',
    'admin.workspaceSchedules.erase_error':     'Det gick inte att radera schemat',
    'admin.workspaceSchedules.empty_user_title':'Inga scheman för den här användaren',
    'admin.workspaceSchedules.empty_title':     'Inga scheman än',
    'admin.workspaceSchedules.empty_body':      'Schemalagda uppgifter som skapas i den här arbetsytan visas här.',

    // ── web-ui operator/OperatorView.vue ──
    'operator.title':                     'Operatör',
    'operator.subtitle':                  'Plattformens backoffice: registreringar, inbjudningar och arbetsytor.',
    'operator.section_aria':              'Operatörssektion',
    'operator.help_docs':                 'Hjälpdokument',
    'operator.tab_signups':               'Registreringar',
    'operator.tab_invites':               'Inbjudningar',
    'operator.tab_tenants':               'Arbetsytor',
    'operator.copied':                    'Kopierat till urklipp',
    'operator.copy_failed':               'Det gick inte att kopiera till urklipp',
    'operator.copy':                      'Kopiera',
    'operator.dismiss':                   'Avfärda',
    'operator.stat_new_signups':          'Nya registreringar',
    'operator.stat_open_invites':         'Öppna inbjudningar',
    'operator.stat_tenants':              'Arbetsytor',
    'operator.stat_users':                'Användare',
    // Signups
    'operator.req_new':                   'Nya',
    'operator.req_invited':               'Inbjudna',
    'operator.req_rejected':              'Avvisade',
    'operator.req_spam':                  'Skräp',
    'operator.req_all':                   'Alla',
    'operator.filter_signups_aria':       'Filtrera registreringar',
    'operator.search_signups_placeholder':'Sök på namn, e-post eller företag',
    'operator.search_signups_aria':       'Sök registreringar',
    'operator.col_who':                   'Vem',
    'operator.col_company':               'Företag',
    'operator.col_channel':               'Kanal',
    'operator.col_when':                  'När',
    'operator.col_status':                'Status',
    'operator.invite':                    'Bjud in',
    'operator.reject':                    'Avvisa',
    'operator.mark_spam':                 'Markera som skräp',
    'operator.no_signups':                'Inga registreringar',
    'operator.no_signups_new':            'Inga nya registreringsförfrågningar just nu.',
    'operator.no_signups_filtered':       'Inga registreringar matchar det här filtret.',
    'operator.invite_create_error':       'Det gick inte att skapa inbjudan',
    'operator.invite_emailed_to':         'Inbjudan mejlad till {email}',
    'operator.invite_created':            'Inbjudan skapad',
    'operator.signup_link_for':           'Registreringslänk för {email}',
    'operator.marked':                    'Markerad som {status}',
    'operator.update_error':              'Det gick inte att uppdatera förfrågan',
    // Invites
    'operator.inv_active':                'Aktiva',
    'operator.inv_revoked':               'Återkallade',
    'operator.inv_all':                   'Alla',
    'operator.filter_invites_aria':       'Filtrera inbjudningar',
    'operator.new_invite':                'Ny inbjudan',
    'operator.field_note':                'Notering',
    'operator.field_note_hint':           'För din egen referens - vem eller vad inbjudan gäller.',
    'operator.field_max_uses':            'Max antal användningar',
    'operator.field_expires':             'Går ut om dagar',
    'operator.field_expires_hint':        '0 betyder att den aldrig går ut.',
    'operator.field_email_to':            'Mejla till',
    'operator.field_email_to_hint':       'Valfritt - mejla inbjudningslänken direkt.',
    'operator.create_invite':             'Skapa inbjudan',
    'operator.invite_emailed':            'Inbjudan mejlad',
    'operator.signup_link':               'Registreringslänk',
    'operator.col_code':                  'Kod',
    'operator.col_note':                  'Notering',
    'operator.col_uses':                  'Användningar',
    'operator.col_expires':               'Går ut',
    'operator.col_state':                 'Tillstånd',
    'operator.col_created':               'Skapad',
    'operator.copy_code':                 'Kopiera kod',
    'operator.joined':                    '{count} gick med',
    'operator.never':                     'Aldrig',
    'operator.state_revoked':             'Återkallad',
    'operator.state_expired':             'Utgången',
    'operator.state_used_up':             'Förbrukad',
    'operator.state_active':              'Aktiv',
    'operator.revoke':                    'Återkalla',
    'operator.revoke_title':              'Återkalla inbjudan?',
    'operator.revoke_body':               'Återkalla inbjudningskod {code}? Den kan inte längre lösas in.',
    'operator.revoke_confirm':            'Återkalla',
    'operator.invite_revoked':            'Inbjudan återkallad',
    'operator.revoke_error':              'Det gick inte att återkalla inbjudan',
    'operator.no_invites':                'Inga inbjudningar',
    'operator.no_invites_body':           'Skapa en inbjudningskod så att någon kan skapa en arbetsyta.',
    // Tenants
    'operator.search_tenants_placeholder':'Sök arbetsytor',
    'operator.search_tenants_aria':       'Sök arbetsytor',
    'operator.new_workspace':             'Ny arbetsyta',
    'operator.field_workspace_name':      'Arbetsytans namn',
    'operator.field_owner_email':         'Ägarens e-post',
    'operator.field_owner_email_hint':    'Den första administratören för den nya arbetsytan.',
    'operator.field_plan_slug':           'Plan',
    'operator.field_plan_slug_hint':      'Valfritt - använder gratisplanen som standard.',
    'operator.field_website':             'Webbplats',
    'operator.field_website_hint':        'Företagets domän, används för varumärkning.',
    'operator.field_workspace_url':       'Arbetsyte-URL',
    'operator.field_workspace_url_hint':  'Valfritt - sluggen i arbetsytans URL.',
    'operator.field_access':              'Åtkomst',
    'operator.field_join_as_admin':       'Gå med som administratör',
    'operator.field_integrations':        'Integrationer',
    'operator.field_enable_tic':          'Aktivera TIC företagsinformation (gratis)',
    'operator.field_tic_hint':            'Förseglar plattformens TIC-nyckel i den nya arbetsytan och slår på integrationen.',
    'operator.tic_enable_failed':         'Arbetsytan skapades, men TIC-integrationen kunde inte aktiveras (kontrollera HQ_TIC_FREEBIE_API_KEY).',
    'operator.create_workspace':          'Skapa arbetsyta',
    'operator.err_name_required':         'Ett arbetsytenamn krävs.',
    'operator.err_owner_required':        'En giltig ägar-e-post krävs.',
    'operator.err_website_required':      'En giltig webbplats krävs.',
    'operator.workspace_create_error':    'Det gick inte att skapa arbetsytan',
    'operator.workspace_created_emailed': 'Arbetsytan {slug} skapad - ägaren mejlad.',
    'operator.workspace_created':         'Arbetsytan {slug} skapad',
    'operator.open_as_admin':             'Öppna {slug} som administratör',
    'operator.owner_signin_link':         'Ägarens inloggningslänk för {slug}',
    'operator.col_workspace':             'Arbetsyta',
    'operator.col_plan':                  'Plan',
    'operator.col_source':                'Källa',
    'operator.col_users':                 'Användare',
    'operator.tag_platform':              'Plattform',
    'operator.tag_demo':                  'Demo',
    'operator.tag_onboarding':            'Onboarding',
    'operator.add_user':                  'Lägg till användare',
    'operator.field_email':               'E-post',
    'operator.field_display_name':        'Visningsnamn',
    'operator.field_role':                'Roll',
    'operator.field_workspace_admin':     'Arbetsyteadministratör',
    'operator.send_invite':               'Skicka inbjudan',
    'operator.cancel':                    'Avbryt',
    'operator.err_email_required':        'En giltig e-post krävs.',
    'operator.add_user_error':            'Det gick inte att lägga till användaren',
    'operator.user_invited':              '{email} inbjuden till {slug}',
    'operator.user_added':                '{email} tillagd i {slug}',
    'operator.user_signin_link':          'Inloggningslänk för {email}',
    'operator.no_tenants':                'Inga arbetsytor',
    'operator.no_tenants_body':           'Skapa en arbetsyta för att ta ombord ett företag.',
  },
};
